import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// 用户信息
export interface User {
  id: number;
  username: string;
  email: string;
  avatar?: string;
  role: 'admin' | 'manager' | 'editor' | 'analyst' | 'operator';
}

// 全局状态类型
interface GlobalState {
  // 用户状态
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  
  // 主题
  theme: 'light' | 'dark' | 'system';
  
  // 侧边栏
  sidebarCollapsed: boolean;
  
  // AI 侧边栏
  aiSidebarOpen: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setAISidebarOpen: (open: boolean) => void;
  toggleAISidebar: () => void;
}

export const useGlobalStore = create<GlobalState>()(
  persist(
    (set) => ({
      // 初始状态
      user: null,
      token: null,
      isAuthenticated: false,
      theme: 'dark',
      sidebarCollapsed: false,
      aiSidebarOpen: false,

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setToken: (token) => set({ token }),
      
      logout: () => set({ user: null, token: null, isAuthenticated: false }),
      
      setTheme: (theme) => {
        set({ theme });
        // 应用主题到 document
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
          root.classList.add(systemTheme);
        } else {
          root.classList.add(theme);
        }
      },
      
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      
      setAISidebarOpen: (open) => set({ aiSidebarOpen: open }),
      
      toggleAISidebar: () => set((state) => ({ aiSidebarOpen: !state.aiSidebarOpen })),
    }),
    {
      name: 'redmix-global-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// 主题初始化
export function initTheme() {
  const { theme } = useGlobalStore.getState();
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  
  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    root.classList.add(systemTheme);
  } else {
    root.classList.add(theme);
  }
}

// 初始化时应用主题
if (typeof window !== 'undefined') {
  initTheme();
}

